import React from "react";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import colors from "../../../colors/colors";
import { EditField } from "../types";
import { formatElementName } from "../utils";
import { GroupedFields } from "../hooks/useEditPanelFields";
import { FieldItem, FieldItemProps } from "./FieldItem";

interface FieldsAreaProps {
  displayedGroupedFields: GroupedFields;
  totalDisplayedFields: number;
  isShowingAllCategories: boolean;
  collapsedGroups: Set<string>;
  toggleGroupCollapse: (groupName: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  fieldItemProps: Omit<FieldItemProps, "field">;
}

const GroupHeader: React.FC<{
  label: string;
  count: number;
  isCollapsed: boolean;
  onClick: () => void;
  marginBottom?: string | number;
}> = ({ label, count, isCollapsed, onClick, marginBottom }) => (
  <button
    className="edit-panel-group-header"
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "12px",
      fontWeight: 700,
      color: colors.primary,
      margin: 0,
      padding: 0,
      paddingBottom: "8px",
      marginBottom: marginBottom ?? 0,
      borderTop: "none",
      borderRight: "none",
      borderLeft: "none",
      borderBottom: `2px solid ${colors.border_glass}`,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      background: "none",
      width: "100%",
      textAlign: "left",
      fontFamily: "inherit",
    }}
  >
    {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
    {label}
    <span
      style={{
        fontSize: "10px",
        fontWeight: 500,
        color: colors.gray_medium,
        marginLeft: "auto",
      }}
    >
      {count}
    </span>
  </button>
);

const FieldGrid: React.FC<{ fields: EditField[]; itemProps: Omit<FieldItemProps, "field"> }> = ({
  fields,
  itemProps,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    }}
  >
    {fields.map((field) => (
      <FieldItem key={field.key} field={field} {...itemProps} />
    ))}
  </div>
);

export const FieldsArea: React.FC<FieldsAreaProps> = ({
  displayedGroupedFields,
  totalDisplayedFields,
  isShowingAllCategories,
  collapsedGroups,
  toggleGroupCollapse,
  searchQuery,
  setSearchQuery,
  fieldItemProps,
}) => {
  if (totalDisplayedFields === 0) {
    return (
      <div
        className="edit-panel-content"
        style={{
          padding: "16px 20px 20px",
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            color: colors.gray_medium,
            gap: "8px",
          }}
        >
          <Search size={24} style={{ opacity: 0.3 }} />
          <span style={{ fontSize: "12px", fontWeight: 500 }}>
            No matching properties
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={{
                background: "none",
                border: `1px solid ${colors.border_glass}`,
                borderRadius: "8px",
                padding: "6px 12px",
                fontSize: "11px",
                color: colors.blue,
                cursor: "pointer",
                fontFamily: "inherit",
                marginTop: "4px",
              }}
            >
              Clear search
            </button>
          )}
        </div>
      </div>
    );
  }

  const hasGroups = Object.keys(displayedGroupedFields.groups).length > 0;

  return (
    <div
      className="edit-panel-content"
      style={{
        padding: "16px 20px 20px",
        overflowY: "auto",
        overflowX: "hidden",
        flex: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Base fields (General) */}
        {displayedGroupedFields.baseFields.length > 0 && (
          <>
            {isShowingAllCategories && hasGroups && (
              <GroupHeader
                label="General"
                count={displayedGroupedFields.baseFields.length}
                isCollapsed={collapsedGroups.has("__general__")}
                onClick={() => toggleGroupCollapse("__general__")}
              />
            )}
            {(!isShowingAllCategories ||
              !collapsedGroups.has("__general__") ||
              !hasGroups) && (
              <FieldGrid
                fields={displayedGroupedFields.baseFields}
                itemProps={fieldItemProps}
              />
            )}
          </>
        )}

        {/* Grouped fields with collapsible section headers */}
        {Object.entries(displayedGroupedFields.groups).map(
          ([groupName, groupFields]) => {
            const isCollapsed =
              isShowingAllCategories && collapsedGroups.has(groupName);
            const showHeader = isShowingAllCategories;

            return (
              <div key={groupName}>
                {showHeader && (
                  <GroupHeader
                    label={formatElementName(groupName)}
                    count={groupFields.length}
                    isCollapsed={isCollapsed}
                    onClick={() => toggleGroupCollapse(groupName)}
                    marginBottom={isCollapsed ? 0 : "16px"}
                  />
                )}
                {!isCollapsed && (
                  <FieldGrid fields={groupFields} itemProps={fieldItemProps} />
                )}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};
